import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useSiteSettings } from './SiteSettingsContext';
import { orderStatusLabel } from './account/orderStatusLabel';
import CustomerShipmentTracking from './account/CustomerShipmentTracking';
import { CustomerOrderPaymentSection } from './account/orderPaymentDisplay';
import AccountOrderReturnRequest from './account/AccountOrderReturnRequest';
import OrderCustomizationDisplay from './jewelry/OrderCustomizationDisplay';
import { brandNameFromSettings, storeSeo } from './siteBrand';

function formatMoney(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export default function TrackOrderPage() {
  const { settings: site } = useSiteSettings();
  const [orderNumber, setOrderNumber] = useState('');
  const [contact, setContact] = useState('');
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [returnNotice, setReturnNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setReturnNotice('');
    setOrder(null);
    setBusy(true);
    try {
      const num = orderNumber.trim().replace(/^#/, '').toUpperCase();
      const contactRaw = contact.trim();
      if (!num || !contactRaw) {
        setError('Enter your order number and email or phone.');
        return;
      }
      const body = { action: 'guest_lookup', order_number: num };
      if (contactRaw.includes('@')) body.email = contactRaw;
      else body.phone = contactRaw;
      const r = await fetch('/api/orders.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      let d = {};
      try {
        d = await r.json();
      } catch {
        setError(
          r.status === 404
            ? 'API not found. Run npm run php:serve in another terminal, then npm start.'
            : 'Invalid response from server. Check that PHP is running locally.'
        );
        return;
      }
      if (!r.ok || !d.success) {
        if (r.status === 503) {
          setError('Database unavailable. Check MySQL and public/includes/db_config.php.');
          return;
        }
        setError(d.error || 'Order not found.');
        return;
      }
      setOrder(d.order);
    } catch {
      setError('Could not look up order. Run npm run php:serve, then try again.');
    } finally {
      setBusy(false);
    }
  };

  const status = order ? orderStatusLabel(order.status) : null;
  const brandName = brandNameFromSettings(site);
  const seo = storeSeo(
    site,
    'seo_track_order_title',
    'Track your order',
    'Look up your order status with your order number and email.'
  );
  const whatsappHref = `${site.whatsapp_href || 'https://wa.me/919680384332'}?text=${encodeURIComponent(
    order
      ? `Hi, I have a question about order #${order.order_number}.`
      : `Hi, I need help finding my ${brandName} order.`
  )}`;

  return (
    <div className="store-page utility-page track-order-page">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container store-page__container">
        <header className="store-page__header utility-page__header">
          <p className="shop-hero-eyebrow">{brandName}</p>
          <div className="utility-page__title-row">
            <div>
              <h1 className="store-page__title">Track your order</h1>
              <p className="store-page__lead mb-0">
                See status, tracking, and items — use the email or phone from checkout.
              </p>
            </div>
          </div>
        </header>

        <div className="utility-page__layout">
          <div className="utility-page__main">
            <form onSubmit={onSubmit} className="utility-card" noValidate>
              <h2 className="utility-card__title h6">Order lookup</h2>
              <div className="mb-3">
                <label className="form-label" htmlFor="track-num">
                  Order number
                </label>
                <input
                  id="track-num"
                  className="form-control"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="e.g. SD250515A1B2C3"
                  autoComplete="off"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="track-contact">
                  Email or phone
                </label>
                <input
                  id="track-contact"
                  className="form-control"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="Email or 10-digit mobile from checkout"
                  autoComplete="email tel"
                  required
                />
              </div>
              {error ? (
                <div className="alert alert-danger py-2 small mb-3" role="alert">
                  {error}
                </div>
              ) : null}
              <button type="submit" className="btn btn-primary rounded-pill w-100 store-cta" disabled={busy}>
                {busy ? 'Looking up…' : 'Find my order'}
              </button>
            </form>

            {order ? (
              <div className="utility-card track-order-result mt-4" aria-live="polite">
                <div className="track-order-result__head">
                  <div>
                    <p className="utility-card__eyebrow mb-1">Order found</p>
                    <h2 className="track-order-result__title h4 mb-1">#{order.order_number}</h2>
                    <p className="small text-muted mb-0">
                      {order.created_at ? new Date(order.created_at).toLocaleString('en-IN') : ''}
                    </p>
                  </div>
                  <span className={status.className}>{status.label}</span>
                </div>

                <p className="track-order-result__total fw-semibold mb-3">
                  Order total: {formatMoney(order.total_amount)}
                </p>

                <CustomerOrderPaymentSection order={order} className="track-order-result__payment" />

                {returnNotice ? (
                  <div className="alert alert-success py-2 small mb-3" role="status">
                    {returnNotice}
                  </div>
                ) : null}

                <AccountOrderReturnRequest
                  detail={order}
                  guestContact={contact}
                  setFeedback={({ kind, text }) => {
                    if (kind === 'error') {
                      setError(text);
                      setReturnNotice('');
                    } else {
                      setReturnNotice(text);
                      setError('');
                    }
                  }}
                  onOrderUpdated={setOrder}
                />

                {order.cancel_requested_at ? (
                  <p className="small text-warning mb-3">
                    Cancellation requested — we will confirm shortly.
                  </p>
                ) : null}

                {Array.isArray(order.shipments) && order.shipments.length > 0 ? (
                  <div className="track-order-result__tracking mb-3">
                    <p className="small fw-semibold mb-2">Shipments</p>
                    {order.shipments.map((s) => (
                      <div key={s.id || s.shipment_seq} className="mb-3">
                        <p className="small mb-1">
                          <strong>Package {s.shipment_seq}</strong>
                        </p>
                        {(s.items || []).length > 0 ? (
                          <ul className="small text-muted ps-3 mb-2">
                            {s.items.map((si, i) => (
                              <li key={`${si.order_item_id}-${i}`}>
                                {si.product_name} × {si.quantity}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        <CustomerShipmentTracking
                          tracking_number={s.tracking_number}
                          tracking_url={s.tracking_url}
                          tracking_carrier_name={s.tracking_carrier_name}
                        />
                      </div>
                    ))}
                  </div>
                ) : order.tracking_number || order.tracking_url ? (
                  <CustomerShipmentTracking
                    className="track-order-result__tracking mb-3"
                    tracking_number={order.tracking_number}
                    tracking_url={order.tracking_url}
                    tracking_carrier_name={order.tracking_carrier_name}
                  />
                ) : null}

                {(order.items || []).length > 0 ? (
                  <ul className="track-order-result__items list-unstyled mb-0">
                    {order.items.map((it, idx) => (
                      <li key={idx} className="track-order-result__line">
                        <span>
                          {it.product_name} × {it.quantity}
                          {it.customization_text ? (
                            <span className="d-block small text-muted">
                              <OrderCustomizationDisplay raw={it.customization_text} />
                            </span>
                          ) : null}
                        </span>
                        <span className="fw-medium">{formatMoney(it.line_total)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>

          <aside className="utility-page__aside" aria-label="Need help?">
            <div className="utility-aside-card">
              <h2 className="utility-aside-card__title h6">Need help?</h2>
              <ul className="utility-aside-card__list">
                <li>Your order number is in the confirmation email or SMS after checkout.</li>
                <li>Use the same email or mobile number you entered when placing the order.</li>
                <li>Signed-in customers can see all orders in My account.</li>
              </ul>
              <Link to="/account" className="btn btn-outline-primary btn-sm rounded-pill w-100 mb-2">
                My account orders
              </Link>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary btn-sm rounded-pill w-100"
              >
                Message on WhatsApp
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
